import React, { useEffect, useRef, useState } from "react";
import "./dash.css";
import * as XLSX from "xlsx";
import { supabase } from "../supabaseClient";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
} from "recharts";

const STATUS_COLORS = ["#a855f7", "#f59e0b", "#10b981", "#ef4444"];
const TYPE_COLORS = ["#0ea5e9", "#f97316", "#8b5cf6"];

const RESPONSIBLE_OPTIONS = [
  "خالد الجابري",
  "اشتياق الهنائي",
  "سالم الخياري",
  "إيمان الحسني",
  "نوار العبري",
  "هدى الذهلي",
];

const normalizeResponsible = (value) => {
  if (Array.isArray(value)) {
    return value.map((name) => String(name).trim()).filter(Boolean);
  }

  if (!value) return [];

  return String(value)
    .split(/[,،]/)
    .map((name) => name.trim())
    .filter(Boolean);
};

const responsibleText = (value) => normalizeResponsible(value).join("، ");

const getFileExtension = (fileName) => {
  const parts = String(fileName || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "bin";
};

const createSafeStorageFileName = (fileName) => {
  const extension = getFileExtension(fileName);

  if (!/^[a-z0-9]+$/i.test(extension)) {
    return `file_${Date.now()}_${Math.round(Math.random() * 1000000000)}.bin`;
  }

  return `file_${Date.now()}_${Math.round(
    Math.random() * 1000000000
  )}.${extension}`;
};

function Dash({ onLogout }) {
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  const [tasks, setTasks] = useState([]);
  const [editId, setEditId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("الكل");
  const [typeFilter, setTypeFilter] = useState("الكل");
  const [responsibleFilter, setResponsibleFilter] = useState("الكل");
  const [loadError, setLoadError] = useState("");
  const [responsibleDropdownOpen, setResponsibleDropdownOpen] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [selectedTask, setSelectedTask] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const [formData, setFormData] = useState({
    period_name: "",
    request_date: "",
    system_name: "",
    correspondence_contact: "",
    task_type: "",
    requester: "",
    status: "",
    category: "",
    responsible: [],
    description: "",
    recommendations: "",
    notes: "",
  });

  const loadUserRole = async () => {
    try {
      const { data, error } = await supabase.rpc("get_my_role");

      if (error) {
        console.error("Role RPC error:", error);
        setUserRole("user");
        return;
      }

      const role = String(data || "user").trim().toLowerCase();
      setUserRole(role);
    } catch (error) {
      console.error("Role load error:", error);
      setUserRole("user");
    }
  };

  const loadTasks = async () => {
    try {
      setLoadError("");

      const { data, error } = await supabase
        .from("tasks")
        .select(`
          *,
          task_attachments (
            id,
            file_name,
            file_path,
            file_size,
            mime_type,
            created_at
          )
        `)
        .order("id", { ascending: false });

      if (error) {
        console.error("Supabase load error:", error);
        setTasks([]);
        setLoadError("لم يتم جلب البيانات من Supabase");
        return;
      }

      const formattedTasks = (data || []).map((task) => ({
        ...task,
        attachments: task.task_attachments || [],
        attachments_count: task.task_attachments?.length || 0,
      }));

      setTasks(formattedTasks);
    } catch (error) {
      console.error("Load tasks error:", error);
      setTasks([]);
      setLoadError("تعذر الاتصال بـ Supabase");
    }
  };

  useEffect(() => {
    loadTasks();
    loadUserRole();
  }, []);

  const goToAddRequest = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleResponsibleToggle = (name) => {
    setFormData((prev) => {
      const currentResponsible = normalizeResponsible(prev.responsible);

      const updatedResponsible = currentResponsible.includes(name)
        ? currentResponsible.filter((item) => item !== name)
        : [...currentResponsible, name];

      return {
        ...prev,
        responsible: updatedResponsible,
      };
    });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setAttachments(selectedFiles);
  };

  const clearForm = () => {
    setEditId(null);
    setAttachments([]);
    setResponsibleDropdownOpen(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setFormData({
      period_name: "",
      request_date: "",
      system_name: "",
      correspondence_contact: "",
      task_type: "",
      requester: "",
      status: "",
      category: "",
      responsible: [],
      description: "",
      recommendations: "",
      notes: "",
    });
  };

  const handleSave = async () => {
    const requiredFields = {
      period_name: "الفترة",
      request_date: "التاريخ",
      system_name: "اسم النظام",
      task_type: "النوع",
      requester: "الجهة الطالبة",
      status: "حالة الطلب",
      category: "الفئة",
      responsible: "اسم المسؤول",
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([key]) => {
        if (key === "responsible") {
          return formData.responsible.length === 0;
        }

        return !String(formData[key] || "").trim();
      })
      .map(([, label]) => label);

    if (missingFields.length > 0) {
      alert(
        "يرجى تعبئة الحقول الإلزامية التالية:\n- " +
          missingFields.join("\n- ")
      );
      return;
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert("يجب تسجيل الدخول أولًا");
        return;
      }

      const taskPayload = {
        user_id: user.id,
        period_name: formData.period_name,
        request_date: formData.request_date,
        system_name: formData.system_name,
        correspondence_contact: formData.correspondence_contact || "",
        task_type: formData.task_type,
        requester: formData.requester,
        status: formData.status,
        category: formData.category,
        responsible: responsibleText(formData.responsible),
        description: formData.description || "",
        recommendations: formData.recommendations || "",
        notes: formData.notes || "",
        updated_at: new Date().toISOString(),
      };

      let taskResult = null;

      if (editId) {
        const { data, error } = await supabase
          .from("tasks")
          .update(taskPayload)
          .eq("id", editId)
          .select()
          .single();

        if (error) {
          console.error("Supabase update error:", error);
          alert("حدث خطأ أثناء تعديل البيانات: " + error.message);
          return;
        }

        taskResult = data;
      } else {
        const { data, error } = await supabase
          .from("tasks")
          .insert(taskPayload)
          .select()
          .single();

        if (error) {
          console.error("Supabase insert error:", error);
          alert("حدث خطأ أثناء حفظ البيانات: " + error.message);
          return;
        }

        taskResult = data;
      }

      if (attachments.length > 0 && taskResult?.id) {
        for (const file of attachments) {
          const storageFileName = createSafeStorageFileName(file.name);
          const filePath = `${taskResult.id}/${storageFileName}`;

          const { error: uploadError } = await supabase.storage
            .from("task-attachments")
            .upload(filePath, file, {
              cacheControl: "3600",
              contentType: file.type || "application/octet-stream",
              upsert: false,
            });

          if (uploadError) {
            console.error("Supabase upload error:", uploadError);
            alert(
              "تم حفظ الطلب، لكن حدث خطأ أثناء رفع المرفق: " +
                uploadError.message
            );
            await loadTasks();
            return;
          }

          const { error: attachmentError } = await supabase
            .from("task_attachments")
            .insert({
              task_id: taskResult.id,
              file_name: file.name,
              file_path: filePath,
              file_size: file.size,
              mime_type: file.type || "application/octet-stream",
            });

          if (attachmentError) {
            console.error("Supabase attachment insert error:", attachmentError);
            alert(
              "تم رفع الملف، لكن حدث خطأ أثناء حفظ بيانات المرفق: " +
                attachmentError.message
            );
            await loadTasks();
            return;
          }
        }
      }

      alert(editId ? "تم تعديل البيانات بنجاح" : "تم حفظ البيانات بنجاح");
      clearForm();
      await loadTasks();
    } catch (error) {
      console.error("Save error:", error);
      alert("حدث خطأ أثناء الاتصال بـ Supabase");
    }
  };

  const handleEdit = (task) => {
    setEditId(task.id);
    setAttachments([]);
    setResponsibleDropdownOpen(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setFormData({
      period_name: task.period_name || "",
      request_date: task.request_date || "",
      system_name: task.system_name || "",
      correspondence_contact: task.correspondence_contact || "",
      task_type: task.task_type || "",
      requester: task.requester || "",
      status: task.status || "",
      category: task.category || "",
      responsible: normalizeResponsible(task.responsible),
      description: task.description || "",
      recommendations: task.recommendations || "",
      notes: task.notes || "",
    });

    goToAddRequest();
  };

  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const canDelete = userRole === "admin" || userRole === "manager";

  const editingTask = editId
    ? safeTasks.find((task) => Number(task.id) === Number(editId))
    : null;

  const editingAttachments = editingTask?.attachments || [];

  const handleOpenAttachment = async (file) => {
    if (!file?.file_path) {
      alert("مسار المرفق غير متوفر");
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from("task-attachments")
        .createSignedUrl(file.file_path, 60);

      if (error) {
        console.error("Open attachment error:", error);
        alert("تعذر فتح المرفق: " + error.message);
        return;
      }

      if (data?.signedUrl) {
        window.open(data.signedUrl, "_blank", "noopener,noreferrer");
      } else {
        alert("تعذر إنشاء رابط لفتح المرفق");
      }
    } catch (error) {
      console.error("Attachment view error:", error);
      alert("حدث خطأ أثناء استعراض المرفق");
    }
  };

  const handleDeleteAttachment = async (file) => {
    if (!canDelete) {
      alert("لا تملك صلاحية حذف المرفقات. يرجى التواصل مع مدير النظام.");
      return;
    }

    if (!file?.id) {
      alert("بيانات المرفق غير مكتملة");
      return;
    }

    if (!window.confirm("هل تريد حذف هذا المرفق؟")) return;

    try {
      const { error: dbError } = await supabase
        .from("task_attachments")
        .delete()
        .eq("id", file.id);

      if (dbError) {
        console.error("Delete attachment database error:", dbError);

        if (dbError.message?.toLowerCase().includes("row-level security")) {
          alert("لا تملك صلاحية حذف المرفقات حسب إعدادات Supabase.");
        } else {
          alert("حدث خطأ أثناء حذف بيانات المرفق: " + dbError.message);
        }

        return;
      }

      if (file.file_path) {
        const { error: storageError } = await supabase.storage
          .from("task-attachments")
          .remove([file.file_path]);

        if (storageError) {
          console.warn("Delete attachment storage warning:", storageError);
        }
      }

      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          const updatedAttachments = (task.attachments || []).filter(
            (attachment) => Number(attachment.id) !== Number(file.id)
          );

          return {
            ...task,
            attachments: updatedAttachments,
            attachments_count: updatedAttachments.length,
          };
        })
      );

      setSelectedTask((prevTask) => {
        if (!prevTask) return prevTask;

        const updatedAttachments = (prevTask.attachments || []).filter(
          (attachment) => Number(attachment.id) !== Number(file.id)
        );

        return {
          ...prevTask,
          attachments: updatedAttachments,
          attachments_count: updatedAttachments.length,
        };
      });

      alert("تم حذف المرفق بنجاح");
      await loadTasks();
    } catch (error) {
      console.error("Delete attachment error:", error);
      alert("حدث خطأ أثناء حذف المرفق");
    }
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setIsViewOpen(true);
  };

  const closeView = () => {
    setSelectedTask(null);
    setIsViewOpen(false);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      alert("لا تملك صلاحية حذف السجلات. يرجى التواصل مع مدير النظام.");
      return;
    }

    if (!window.confirm("هل تريد حذف هذا السجل؟")) return;

    try {
      const task = safeTasks.find((item) => Number(item.id) === Number(id));

      const paths = (task?.attachments || [])
        .map((file) => file.file_path)
        .filter(Boolean);

      const { error: deleteError } = await supabase
        .from("tasks")
        .delete()
        .eq("id", id);

      if (deleteError) {
        console.error("Supabase delete error:", deleteError);

        if (deleteError.message?.toLowerCase().includes("row-level security")) {
          alert(
            "لا تملك صلاحية الحذف حسب إعدادات Supabase. تأكد أن دورك admin أو manager في جدول user_roles."
          );
        } else {
          alert("حدث خطأ أثناء حذف البيانات: " + deleteError.message);
        }

        return;
      }

      if (paths.length > 0) {
        const { error: removeError } = await supabase.storage
          .from("task-attachments")
          .remove(paths);

        if (removeError) {
          console.warn("Storage remove warning:", removeError);
        }
      }

      alert("تم حذف السجل بنجاح");
      await loadTasks();
    } catch (error) {
      console.error("Delete error:", error);
      alert("تعذر حذف السجل من Supabase");
    }
  };

  const handleExportExcel = () => {
    if (safeTasks.length === 0) {
      alert("لا توجد بيانات للتصدير");
      return;
    }

    const exportData = safeTasks.map((task, index) => ({
      الرقم: index + 1,
      الفترة: task.period_name,
      "تاريخ الطلب": task.request_date,
      "اسم النظام": task.system_name,
      "رقم المراسلة / البريد الإلكتروني": task.correspondence_contact || "",
      النوع: task.task_type,
      "الجهة الطالبة": task.requester,
      "حالة الطلب": task.status,
      الفئة: task.category,
      المسؤول: responsibleText(task.responsible),
      "وصف الطلب": task.description,
      الهدف: task.recommendations,
      الملاحظات: task.notes,
      "عدد المرفقات": task.attachments_count || 0,
      "تاريخ الإنشاء": task.created_at,
      "تاريخ التحديث": task.updated_at,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "المهام");

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 14 },
      { wch: 22 },
      { wch: 28 },
      { wch: 14 },
      { wch: 20 },
      { wch: 16 },
      { wch: 12 },
      { wch: 24 },
      { wch: 28 },
      { wch: 28 },
      { wch: 28 },
      { wch: 14 },
      { wch: 22 },
      { wch: 22 },
    ];

    XLSX.writeFile(
      workbook,
      `مهام_القسم_${new Date().toLocaleDateString("ar")}.xlsx`
    );
  };

  const filteredTasks = safeTasks.filter((task) => {
    const searchValue = searchText.trim().toLowerCase();
    const taskResponsibleList = normalizeResponsible(task.responsible);
    const taskResponsibleSearchText = taskResponsibleList
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      searchValue === "" ||
      (task.system_name || "").toLowerCase().includes(searchValue) ||
      (task.correspondence_contact || "").toLowerCase().includes(searchValue) ||
      (task.requester || "").toLowerCase().includes(searchValue) ||
      taskResponsibleSearchText.includes(searchValue);

    const matchesStatus =
      statusFilter === "الكل" || task.status === statusFilter;

    const matchesType =
      typeFilter === "الكل" || task.task_type === typeFilter;

    const matchesResponsible =
      responsibleFilter === "الكل" ||
      taskResponsibleList.includes(responsibleFilter);

    return matchesSearch && matchesStatus && matchesType && matchesResponsible;
  });

  const totalTasks = safeTasks.length;
  const inProgressTasks = safeTasks.filter(
    (t) => t.status === "قيد التنفيذ"
  ).length;
  const completedTasks = safeTasks.filter((t) => t.status === "مكتمل").length;
  const lateTasks = safeTasks.filter((t) => t.status === "متأخر").length;

  const statusChartData = [
    {
      name: "جديد",
      value: safeTasks.filter((t) => t.status === "جديد").length,
    },
    {
      name: "قيد التنفيذ",
      value: safeTasks.filter((t) => t.status === "قيد التنفيذ").length,
    },
    {
      name: "مكتمل",
      value: safeTasks.filter((t) => t.status === "مكتمل").length,
    },
    {
      name: "متأخر",
      value: safeTasks.filter((t) => t.status === "متأخر").length,
    },
  ].filter((d) => d.value > 0);

  const typeChartData = ["web", "update", "application"]
    .map((type) => ({
      name: type,
      value: safeTasks.filter((t) => t.task_type === type).length,
    }))
    .filter((d) => d.value > 0);

  const responsibleChartData = RESPONSIBLE_OPTIONS.map((fullName) => {
    const personTasks = safeTasks.filter((task) =>
      normalizeResponsible(task.responsible).includes(fullName)
    );

    const اختبار = personTasks.filter((t) => t.category === "اختبار").length;
    const تحليل = personTasks.filter((t) => t.category === "تحليل").length;
    const تدريب = personTasks.filter((t) => t.category === "تدريب").length;

    return {
      name: fullName.split(" ")[0],
      اختبار,
      تحليل,
      تدريب,
      total: اختبار + تحليل + تدريب,
    };
  }).filter((d) => d.total > 0);

  const categoryChartData = ["اختبار", "تحليل", "تدريب"]
    .map((cat) => {
      const categoryTasks = safeTasks.filter((t) => t.category === cat);

      const جديد = categoryTasks.filter((t) => t.status === "جديد").length;
      const قيد_التنفيذ = categoryTasks.filter(
        (t) => t.status === "قيد التنفيذ"
      ).length;
      const مكتمل = categoryTasks.filter((t) => t.status === "مكتمل").length;

      return {
        name: cat,
        جديد,
        قيد_التنفيذ,
        مكتمل,
        total: جديد + قيد_التنفيذ + مكتمل,
      };
    })
    .filter((d) => d.total > 0);

  const renderCustomPieLabel = (props) => {
    const { cx, cy, midAngle, outerRadius, percent } = props;

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 18;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#111827"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={14}
        fontWeight="700"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="dash-page" dir="rtl">
      <div className="dash-container">
        <div className="dash-header">
          <div className="dash-circle dash-circle-left"></div>
          <div className="dash-circle dash-circle-right"></div>

          <h1>مُؤَشِّرَاتُ قِسْمِ اخْتِبَارِ وَتَحْلِيلِ الْأَنْظِمَةِ</h1>
          <p>نظام إلكتروني لمتابعة مهام الاختبار والتحليل والتقارير الخاصة بالقسم</p>

          <div className="dash-header-buttons">
            <button type="button" className="logout-btn" onClick={onLogout}>
              تسجيل الخروج
            </button>
          </div>
        </div>

        {loadError && (
          <div
            style={{
              background: "#fff3cd",
              color: "#856404",
              border: "1px solid #ffeeba",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "12px",
              textAlign: "center",
              fontWeight: "700",
            }}
          >
            {loadError}
          </div>
        )}

        <div className="dash-cards">
          <div className="dash-card purple">
            <span>إجمالي السجلات</span>
            <strong>{totalTasks}</strong>
            <small>طلب مسجل</small>
          </div>

          <div className="dash-card orange">
            <span>المهام قيد التنفيذ</span>
            <strong>{inProgressTasks}</strong>
            <small>تحت المعالجة</small>
          </div>

          <div className="dash-card green">
            <span>المهام المكتملة</span>
            <strong>{completedTasks}</strong>
            <small>تم إنجازها</small>
          </div>

          <div className="dash-card blue">
            <span>المهام المتأخرة</span>
            <strong>{lateTasks}</strong>
            <small>تحتاج متابعة</small>
          </div>
        </div>

        <section className="dash-section" ref={formRef}>
          <h2>{editId ? "تعديل بيانات المهمة" : "إدخال مهام القسم للعام 2026 م"}</h2>

          <div className="dash-form-grid">
            <div className="field">
              <label>
                الفترة <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="period_name"
                value={formData.period_name}
                onChange={handleChange}
                required
              >
                <option value="">اختر الفترة</option>
                <option value="الأولى">الأولى</option>
                <option value="الثانية">الثانية</option>
              </select>
            </div>

            <div className="field">
              <label>
                تاريخ <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="date"
                name="request_date"
                value={formData.request_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="field">
              <label>
                اسم النظام <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="system_name"
                placeholder="أدخل اسم النظام أو الخدمة"
                value={formData.system_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="field">
              <label>رقم المراسلة / البريد الإلكتروني</label>
              <input
                type="text"
                name="correspondence_contact"
                placeholder="أدخل رقم المراسلة أو البريد الإلكتروني"
                value={formData.correspondence_contact}
                onChange={handleChange}
              />
            </div>

            <div className="field">
              <label>
                النوع <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="task_type"
                value={formData.task_type}
                onChange={handleChange}
                required
              >
                <option value="">اختر النوع</option>
                <option value="web">web</option>
                <option value="update">update</option>
                <option value="application">application</option>
              </select>
            </div>

            <div className="field">
              <label>
                الجهة الطالبة <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="requester"
                placeholder="اسم الجهة الطالبة"
                value={formData.requester}
                onChange={handleChange}
                required
              />
            </div>

            <div className="field">
              <label>
                حالة الطلب <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="">اختر حالة الطلب</option>
                <option value="جديد">جديد</option>
                <option value="قيد التنفيذ">قيد التنفيذ</option>
                <option value="مكتمل">مكتمل</option>
                <option value="متأخر">متأخر</option>
              </select>
            </div>

            <div className="field">
              <label>
                الفئة <span style={{ color: "red" }}>*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">اختر الفئة</option>
                <option value="اختبار">اختبار</option>
                <option value="تحليل">تحليل</option>
                <option value="تدريب">تدريب</option>
              </select>
            </div>

            <div className="field">
              <label>
                اسم المسؤول <span style={{ color: "red" }}>*</span>
              </label>

              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setResponsibleDropdownOpen((prev) => !prev)}
                  style={{
                    width: "100%",
                    minHeight: "42px",
                    height: "auto",
                    border: "1px solid #cfdde7",
                    borderRadius: "7px",
                    padding: "9px 10px",
                    background: "#ffffff",
                    color:
                      formData.responsible.length > 0 ? "#1f2937" : "#777",
                    fontSize: "13px",
                    fontWeight: "700",
                    textAlign: "right",
                    cursor: "pointer",
                    boxShadow: "none",
                    margin: 0,
                  }}
                >
                  {formData.responsible.length > 0
                    ? responsibleText(formData.responsible)
                    : "اختر اسم المسؤول"}
                  <span style={{ float: "left" }}>▾</span>
                </button>

                {responsibleDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "48px",
                      right: 0,
                      left: 0,
                      zIndex: 50,
                      background: "#ffffff",
                      border: "1px solid #cfdde7",
                      borderRadius: "8px",
                      boxShadow: "0 8px 18px rgba(0,0,0,0.12)",
                      padding: "8px",
                      maxHeight: "240px",
                      overflowY: "auto",
                    }}
                  >
                    {RESPONSIBLE_OPTIONS.map((name) => (
                      <label
                        key={name}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          color: "#1f2937",
                          fontSize: "13px",
                          fontWeight: "700",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.responsible.includes(name)}
                          onChange={() => handleResponsibleToggle(name)}
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                          }}
                        />
                        {name}
                      </label>
                    ))}

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        borderTop: "1px solid #eef2f7",
                        paddingTop: "8px",
                        marginTop: "6px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setResponsibleDropdownOpen(false)}
                        style={{
                          width: "auto",
                          height: "auto",
                          padding: "6px 12px",
                          border: 0,
                          borderRadius: "6px",
                          background: "#0f5d89",
                          color: "#ffffff",
                          fontSize: "12px",
                          fontWeight: "800",
                          cursor: "pointer",
                          margin: 0,
                        }}
                      >
                        تم
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            responsible: [],
                          }))
                        }
                        style={{
                          width: "auto",
                          height: "auto",
                          padding: "6px 12px",
                          border: "1px solid #cfdde7",
                          borderRadius: "6px",
                          background: "#f8fafc",
                          color: "#334155",
                          fontSize: "12px",
                          fontWeight: "800",
                          cursor: "pointer",
                          margin: 0,
                        }}
                      >
                        مسح الاختيار
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="field">
              <label>المرفقات</label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
              />

              {attachments.length > 0 && (
                <small
                  style={{
                    marginTop: "6px",
                    color: "#0f5d89",
                    fontWeight: "700",
                  }}
                >
                  عدد الملفات المختارة: {attachments.length}
                </small>
              )}

              {editId && editingAttachments.length > 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "10px",
                  }}
                >
                  <strong style={{ color: "#0f5d89", fontSize: "13px" }}>
                    المرفقات الحالية
                  </strong>

                  <ul style={{ margin: "8px 0 0", paddingRight: "18px" }}>
                    {editingAttachments.map((file) => (
                      <li
                        key={file.id}
                        style={{
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        <span style={{ fontSize: "12px", color: "#334155" }}>
                          {file.file_name || file.file_path}
                        </span>

                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleOpenAttachment(file)}
                            style={{
                              width: "auto",
                              height: "auto",
                              padding: "5px 10px",
                              border: 0,
                              borderRadius: "6px",
                              background: "#0ea5e9",
                              color: "#ffffff",
                              fontSize: "11px",
                              fontWeight: "800",
                              cursor: "pointer",
                              margin: 0,
                              whiteSpace: "nowrap",
                            }}
                          >
                            استعراض المرفق
                          </button>

                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => handleDeleteAttachment(file)}
                              style={{
                                width: "auto",
                                height: "auto",
                                padding: "5px 10px",
                                border: 0,
                                borderRadius: "6px",
                                background: "#ef4444",
                                color: "#ffffff",
                                fontSize: "11px",
                                fontWeight: "800",
                                cursor: "pointer",
                                margin: 0,
                                whiteSpace: "nowrap",
                              }}
                            >
                              حذف المرفق
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="field textarea-field">
              <label>وصف الطلب</label>
              <textarea
                name="description"
                placeholder="أدخل وصف مختصر للطلب"
                value={formData.description}
                onChange={handleChange}
              ></textarea>
            </div>

            <div className="field textarea-field">
              <label>الهدف</label>
              <textarea
                name="recommendations"
                placeholder="أدخل الهدف من الطلب"
                value={formData.recommendations}
                onChange={handleChange}
              ></textarea>
            </div>

            <div className="field textarea-field">
              <label>الملاحظات</label>
              <textarea
                name="notes"
                placeholder="أدخل الملاحظات الخاصة بالطلب"
                value={formData.notes}
                onChange={handleChange}
              ></textarea>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="save-btn" onClick={handleSave}>
              {editId ? "حفظ التعديل" : "حفظ الطلب"}
            </button>

            <button type="button" className="clear-btn" onClick={clearForm}>
              مسح الحقول
            </button>
          </div>
        </section>

        <section className="dash-section">
          <h2>البحث والتصفية</h2>

          <div className="search-grid">
            <div className="field">
              <label>بحث</label>
              <input
                type="text"
                placeholder="ابحث باسم النظام أو الجهة أو المسؤول..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div className="field">
              <label>حالة الطلب</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option>الكل</option>
                <option>جديد</option>
                <option>قيد التنفيذ</option>
                <option>مكتمل</option>
                <option>متأخر</option>
              </select>
            </div>

            <div className="field">
              <label>النوع</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option>الكل</option>
                <option>web</option>
                <option>update</option>
                <option>application</option>
              </select>
            </div>

            <div className="field">
              <label>اسم المسؤول</label>
              <select
                value={responsibleFilter}
                onChange={(e) => setResponsibleFilter(e.target.value)}
              >
                <option>الكل</option>
                {RESPONSIBLE_OPTIONS.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="dash-section">
          <h2>نتائج البحث والتصفية</h2>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>رقم</th>
                  <th>تاريخ الطلب</th>
                  <th>اسم النظام</th>
                  <th>رقم المراسلة / البريد الإلكتروني</th>
                  <th>النوع</th>
                  <th>الحالة</th>
                  <th>المسؤول</th>
                  <th>الفئة</th>
                  <th>ملاحظات</th>
                  <th>مرفقات</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>

              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="11">لا توجد نتائج مطابقة للبحث</td>
                  </tr>
                ) : (
                  filteredTasks.map((task, index) => (
                    <tr key={task.id}>
                      <td>{index + 1}</td>
                      <td>{task.request_date}</td>
                      <td>{task.system_name}</td>
                      <td>{task.correspondence_contact || ""}</td>
                      <td>{task.task_type}</td>
                      <td>{task.status}</td>
                      <td>{responsibleText(task.responsible)}</td>
                      <td>{task.category}</td>
                      <td>{task.notes}</td>
                      <td>{task.attachments_count || 0}</td>
                      <td>
                        <button type="button" onClick={() => handleEdit(task)}>
                          تعديل
                        </button>

                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => handleDelete(task.id)}
                          >
                            حذف
                          </button>
                        )}

                        <button type="button" onClick={() => handleView(task)}>
                          عرض
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              marginTop: "14px",
            }}
          >
            <button
              type="button"
              onClick={handleExportExcel}
              style={{
                width: "auto",
                height: "auto",
                padding: "9px 18px",
                border: 0,
                borderRadius: "8px",
                background: "#0f5d89",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "800",
                cursor: "pointer",
                boxShadow: "0 4px 10px rgba(15, 93, 137, 0.25)",
              }}
            >
              تصدير البيانات
            </button>
          </div>
        </section>

        {isViewOpen && selectedTask && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.45)",
              zIndex: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
            }}
            onClick={closeView}
          >
            <div
              dir="rtl"
              style={{
                width: "min(850px, 100%)",
                maxHeight: "90vh",
                overflowY: "auto",
                background: "#ffffff",
                borderRadius: "14px",
                padding: "20px",
                boxShadow: "0 18px 45px rgba(0,0,0,0.25)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "12px",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    color: "#0f5d89",
                    fontSize: "22px",
                  }}
                >
                  عرض تفاصيل السجل
                </h2>

                <button
                  type="button"
                  onClick={closeView}
                  style={{
                    width: "auto",
                    height: "auto",
                    padding: "7px 14px",
                    border: 0,
                    borderRadius: "8px",
                    background: "#ef4444",
                    color: "#ffffff",
                    fontWeight: "800",
                    cursor: "pointer",
                    margin: 0,
                  }}
                >
                  إغلاق
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>الفترة</strong>
                  <p>{selectedTask.period_name || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>تاريخ الطلب</strong>
                  <p>{selectedTask.request_date || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>اسم النظام</strong>
                  <p>{selectedTask.system_name || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>رقم المراسلة / البريد الإلكتروني</strong>
                  <p>{selectedTask.correspondence_contact || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>النوع</strong>
                  <p>{selectedTask.task_type || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>الجهة الطالبة</strong>
                  <p>{selectedTask.requester || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>حالة الطلب</strong>
                  <p>{selectedTask.status || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>الفئة</strong>
                  <p>{selectedTask.category || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>المسؤول</strong>
                  <p>{responsibleText(selectedTask.responsible) || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>عدد المرفقات</strong>
                  <p>{selectedTask.attachments_count || 0}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>تاريخ الإنشاء</strong>
                  <p>{selectedTask.created_at || "-"}</p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "12px",
                    borderRadius: "10px",
                  }}
                >
                  <strong>تاريخ التحديث</strong>
                  <p>{selectedTask.updated_at || "-"}</p>
                </div>
              </div>

              <div
                style={{
                  marginTop: "12px",
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <strong>وصف الطلب</strong>
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {selectedTask.description || "-"}
                </p>
              </div>

              <div
                style={{
                  marginTop: "12px",
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <strong>الهدف</strong>
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {selectedTask.recommendations || "-"}
                </p>
              </div>

              <div
                style={{
                  marginTop: "12px",
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <strong>الملاحظات</strong>
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {selectedTask.notes || "-"}
                </p>
              </div>

              <div
                style={{
                  marginTop: "12px",
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <strong>المرفقات</strong>

                {selectedTask.attachments &&
                selectedTask.attachments.length > 0 ? (
                  <ul style={{ margin: "10px 0 0", paddingRight: "20px" }}>
                    {selectedTask.attachments.map((file) => (
                      <li
                        key={file.id}
                        style={{
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                        }}
                      >
                        <span>{file.file_name || file.file_path}</span>

                        <button
                          type="button"
                          onClick={() => handleOpenAttachment(file)}
                          style={{
                            width: "auto",
                            height: "auto",
                            padding: "5px 10px",
                            border: 0,
                            borderRadius: "6px",
                            background: "#0ea5e9",
                            color: "#ffffff",
                            fontSize: "12px",
                            fontWeight: "800",
                            cursor: "pointer",
                            margin: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          استعراض المرفق
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>لا توجد مرفقات</p>
                )}
              </div>
            </div>
          </div>
        )}

        <section className="dash-section">
          <h2>المؤشرات على مستوى الأنظمة</h2>

          {safeTasks.length === 0 ? (
            <p className="empty-text">
              يتم تحديث المؤشرات تلقائيًا بناءً على البيانات المحفوظة.
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "32px",
                marginTop: "10px",
              }}
            >
              <div>
                <h3
                  style={{
                    textAlign: "center",
                    color: "#1d5d83",
                    fontSize: "17px",
                    fontWeight: "800",
                    marginBottom: "8px",
                  }}
                >
                  توزيع حالات الطلبات
                </h3>

                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      dataKey="value"
                      labelLine={true}
                      label={renderCustomPieLabel}
                    >
                      {statusChartData.map((entry, i) => (
                        <Cell
                          key={`status-${i}`}
                          fill={STATUS_COLORS[i % STATUS_COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip
                      contentStyle={{
                        fontSize: "14px",
                        fontWeight: "700",
                        textAlign: "right",
                      }}
                      itemStyle={{ color: "#111827", fontWeight: "700" }}
                    />

                    <Legend
                      wrapperStyle={{
                        fontSize: "14px",
                        fontWeight: "700",
                        paddingTop: "10px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h3
                  style={{
                    textAlign: "center",
                    color: "#1d5d83",
                    fontSize: "17px",
                    fontWeight: "800",
                    marginBottom: "8px",
                  }}
                >
                  توزيع أنواع المهام
                </h3>

                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={typeChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      dataKey="value"
                      labelLine={true}
                      label={renderCustomPieLabel}
                    >
                      {typeChartData.map((entry, i) => (
                        <Cell
                          key={`type-${i}`}
                          fill={TYPE_COLORS[i % TYPE_COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip
                      contentStyle={{
                        fontSize: "14px",
                        fontWeight: "700",
                        textAlign: "right",
                      }}
                      itemStyle={{ color: "#111827", fontWeight: "700" }}
                    />

                    <Legend
                      wrapperStyle={{
                        fontSize: "14px",
                        fontWeight: "700",
                        paddingTop: "10px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h3
                  style={{
                    textAlign: "center",
                    color: "#1d5d83",
                    fontSize: "17px",
                    fontWeight: "800",
                    marginBottom: "8px",
                  }}
                >
                  المهام حسب المسؤول
                </h3>

                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={responsibleChartData}
                    margin={{ top: 30, right: 10, left: 10, bottom: 45 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                      dataKey="name"
                      angle={-20}
                      textAnchor="end"
                      tick={{
                        fontSize: 13,
                        fontWeight: "700",
                        fill: "#0f172a",
                      }}
                    />

                    <YAxis
                      allowDecimals={false}
                      tick={{
                        fontSize: 13,
                        fontWeight: "700",
                        fill: "#0f172a",
                      }}
                    />

                    <Tooltip
                      contentStyle={{
                        fontSize: "14px",
                        fontWeight: "700",
                        textAlign: "right",
                      }}
                      itemStyle={{ color: "#111827", fontWeight: "700" }}
                    />

                    <Legend
                      wrapperStyle={{ fontSize: "14px", fontWeight: "700" }}
                    />

                    <Bar
                      dataKey="اختبار"
                      stackId="a"
                      fill="#3b82f6"
                      name="اختبار"
                    />

                    <Bar
                      dataKey="تحليل"
                      stackId="a"
                      fill="#10b981"
                      name="تحليل"
                    />

                    <Bar
                      dataKey="تدريب"
                      stackId="a"
                      fill="#f59e0b"
                      name="تدريب"
                    />

                    <Bar dataKey="total" fill="rgba(0,0,0,0)" legendType="none">
                      <LabelList
                        dataKey="total"
                        position="top"
                        style={{
                          fill: "#111827",
                          fontSize: 14,
                          fontWeight: "800",
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h3
                  style={{
                    textAlign: "center",
                    color: "#1d5d83",
                    fontSize: "17px",
                    fontWeight: "800",
                    marginBottom: "8px",
                  }}
                >
                  المهام حسب الفئة
                </h3>

                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={categoryChartData}
                    margin={{ top: 30, right: 10, left: 10, bottom: 15 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                      dataKey="name"
                      tick={{
                        fontSize: 13,
                        fontWeight: "700",
                        fill: "#0f172a",
                      }}
                    />

                    <YAxis
                      allowDecimals={false}
                      tick={{
                        fontSize: 13,
                        fontWeight: "700",
                        fill: "#0f172a",
                      }}
                    />

                    <Tooltip
                      contentStyle={{
                        fontSize: "14px",
                        fontWeight: "700",
                        textAlign: "right",
                      }}
                      itemStyle={{ color: "#111827", fontWeight: "700" }}
                    />

                    <Legend
                      wrapperStyle={{ fontSize: "14px", fontWeight: "700" }}
                    />

                    <Bar
                      dataKey="جديد"
                      stackId="a"
                      fill="#a855f7"
                      name="جديد"
                    />

                    <Bar
                      dataKey="قيد_التنفيذ"
                      stackId="a"
                      fill="#f59e0b"
                      name="قيد التنفيذ"
                    />

                    <Bar
                      dataKey="مكتمل"
                      stackId="a"
                      fill="#10b981"
                      name="مكتمل"
                    />

                    <Bar dataKey="total" fill="rgba(0,0,0,0)" legendType="none">
                      <LabelList
                        dataKey="total"
                        position="top"
                        style={{
                          fill: "#111827",
                          fontSize: 14,
                          fontWeight: "800",
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Dash;